import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { RichTextEditor } from '@/components/RichTextEditor';
import { toast } from 'sonner';
import { Save, Upload, Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveImg(src) {
  if (!src) return '';
  // Pass through absolute external URLs and base64 data URIs as-is.
  // Only relative paths (e.g. legacy /api/uploads/...) need BACKEND_URL prefixed.
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  return `${BACKEND_URL}${src}`;
}

function HomepageContentEditor() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/admin/homepage-content`, { withCredentials: true })
      .then((r) => setContent(r.data))
      .catch(() => toast.error('Failed to load homepage content'))
      .finally(() => setLoading(false));
  }, []);

  const set = (field, value) => setContent((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { content_id, updated_at, updated_by, ...fields } = content;
      await axios.put(`${BACKEND_URL}/api/admin/homepage-content`, fields, { withCredentials: true });
      toast.success('Homepage content saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/homepage-content/upload-image`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set(field, res.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [field]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!content) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="homepage-content-editor">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/dashboard')} className="text-[#666] hover:text-[#222] transition-colors" data-testid="back-to-dashboard-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#222]">Homepage Content</h1>
          <p className="text-sm text-[#666]">Edit the public homepage text, images, and layout</p>
        </div>
        <SaveBtn saving={saving} onClick={handleSave} testId="save-homepage-btn" />
      </div>

      <div className="space-y-8">
        {/* ===== HERO ===== */}
        <SectionCard title="Hero">
          <Field label="Headline">
            <Input data-testid="edit-hero-headline" value={content.hero_headline || ''} onChange={(e) => set('hero_headline', e.target.value)} />
          </Field>
          <Field label="Body (rich text)">
            <RichTextEditor value={content.hero_body || ''} onChange={(v) => set('hero_body', v)} placeholder="Hero description..." />
          </Field>
          <Field label="Supporting text">
            <Input data-testid="edit-hero-subtext" value={content.hero_subtext || ''} onChange={(e) => set('hero_subtext', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="CTA Button Text">
              <Input data-testid="edit-hero-cta-text" value={content.hero_primary_cta_text || ''} onChange={(e) => set('hero_primary_cta_text', e.target.value)} />
            </Field>
            <Field label="CTA Button URL">
              <Input data-testid="edit-hero-cta-url" value={content.hero_primary_cta_url || ''} onChange={(e) => set('hero_primary_cta_url', e.target.value)} />
            </Field>
          </div>
        </SectionCard>

        {/* ===== IMAGES / MEDIA ===== */}
        <SectionCard title="Images / Media">
          <ImageField label="Hero Background Image" field="hero_image" value={content.hero_image} uploading={uploading.hero_image} onUpload={handleImageUpload} />
          <ImageField label="Job Seekers Section Image" field="job_seekers_image" value={content.job_seekers_image} uploading={uploading.job_seekers_image} onUpload={handleImageUpload} />
          <ImageField label="Employers Section Image" field="employers_image" value={content.employers_image} uploading={uploading.employers_image} onUpload={handleImageUpload} />
          <ImageField label="Communities Section Image" field="communities_image" value={content.communities_image} uploading={uploading.communities_image} onUpload={handleImageUpload} />
        </SectionCard>

        {/* ===== WHY SERGEANT TAP ===== */}
        <SectionCard title="Why Sergeant TAP?">
          <Field label="Section Title">
            <Input data-testid="edit-feature-section-title" value={content.feature_section_title || ''} onChange={(e) => set('feature_section_title', e.target.value)} />
          </Field>
          {[1, 2, 3].map((n) => (
            <div key={n} className="p-4 bg-[#fafafa] rounded-lg border border-[#eee] space-y-3">
              <p className="text-xs font-semibold text-[#999] uppercase tracking-wider">Card {n}</p>
              <Field label="Title">
                <Input data-testid={`edit-feature-card-${n}-title`} value={content[`feature_card_${n}_title`] || ''} onChange={(e) => set(`feature_card_${n}_title`, e.target.value)} />
              </Field>
              <Field label="Body (rich text)">
                <RichTextEditor value={content[`feature_card_${n}_body`] || ''} onChange={(v) => set(`feature_card_${n}_body`, v)} placeholder={`Card ${n} description...`} />
              </Field>
            </div>
          ))}
        </SectionCard>

        {/* ===== FOR SERVICE MEMBERS ===== */}
        <AudienceSectionEditor
          sectionTitle="For Service Members and Military Families"
          prefix="job_seekers"
          content={content}
          set={set}
        />

        {/* ===== WHY MILITARY TALENT MATTERS ===== */}
        <SectionCard title="Why Military Talent Matters">
          <Field label="Section Title">
            <Input data-testid="edit-talent-section-title" value={content.talent_section_title || ''} onChange={(e) => set('talent_section_title', e.target.value)} />
          </Field>
          <Field label="Introduction">
            <Textarea data-testid="edit-talent-section-intro" rows={3} value={content.talent_section_intro || ''} onChange={(e) => set('talent_section_intro', e.target.value)} />
          </Field>
          <div className="space-y-4 mt-2">
            {Array.from({ length: 14 }, (_, idx) => idx + 1).map((n) => (
              <div key={n} className="border border-[#eee] rounded-md p-3">
                <p className="text-xs font-semibold text-[#888] mb-2">Value {n}</p>
                <Field label="Title">
                  <Input data-testid={`edit-talent-value-${n}-title`} value={content[`talent_value_${n}_title`] || ''} onChange={(e) => set(`talent_value_${n}_title`, e.target.value)} />
                </Field>
                <Field label="Description">
                  <Textarea data-testid={`edit-talent-value-${n}-description`} rows={2} value={content[`talent_value_${n}_description`] || ''} onChange={(e) => set(`talent_value_${n}_description`, e.target.value)} />
                </Field>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ===== FOR EMPLOYERS ===== */}
        <AudienceSectionEditor
          sectionTitle="For Employers"
          prefix="employers"
          content={content}
          set={set}
        />

        {/* ===== FOR COMMUNITIES & WORKFORCE LEADERS ===== */}
        <AudienceSectionEditor
          sectionTitle="For Communities & Workforce Leaders"
          prefix="communities"
          content={content}
          set={set}
        />

        {/* ===== WHY SERGEANT TAP EXISTS ===== */}
        <SectionCard title="Why Sergeant TAP Exists">
          <Field label="Section Title">
            <Input data-testid="edit-mission-section-title" value={content.mission_section_title || ''} onChange={(e) => set('mission_section_title', e.target.value)} />
          </Field>
          <Field label="Body (rich text)">
            <RichTextEditor value={content.mission_section_body || ''} onChange={(v) => set('mission_section_body', v)} placeholder="Mission description..." />
          </Field>
        </SectionCard>

        {/* ===== PARTNERSHIP INQUIRY FORM ===== */}
        <SectionCard title="Partnership Inquiry Form">
          <Field label="Form Title">
            <Input data-testid="edit-partner-form-title" value={content.partner_form_title || ''} onChange={(e) => set('partner_form_title', e.target.value)} />
          </Field>
          <Field label="Introduction">
            <Textarea data-testid="edit-partner-form-intro" rows={3} value={content.partner_form_intro || ''} onChange={(e) => set('partner_form_intro', e.target.value)} />
          </Field>
          <Field label="Success Message">
            <Textarea data-testid="edit-partner-form-success" rows={2} value={content.partner_form_success_message || ''} onChange={(e) => set('partner_form_success_message', e.target.value)} />
          </Field>
        </SectionCard>

        {/* ===== BOTTOM CTA ===== */}
        <SectionCard title="Bottom CTA">
          <Field label="Headline">
            <Input data-testid="edit-bottom-cta-headline" value={content.bottom_cta_headline || ''} onChange={(e) => set('bottom_cta_headline', e.target.value)} />
          </Field>
          <Field label="Body (rich text)">
            <RichTextEditor value={content.bottom_cta_body || ''} onChange={(v) => set('bottom_cta_body', v)} placeholder="CTA description..." />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Button Text">
              <Input data-testid="edit-bottom-cta-btn-text" value={content.bottom_cta_button_text || ''} onChange={(e) => set('bottom_cta_button_text', e.target.value)} />
            </Field>
            <Field label="Button URL">
              <Input data-testid="edit-bottom-cta-btn-url" value={content.bottom_cta_button_url || ''} onChange={(e) => set('bottom_cta_button_url', e.target.value)} />
            </Field>
          </div>
        </SectionCard>

        {/* ===== FOOTER ===== */}
        <SectionCard title="Footer">
          <Field label="Footer Tagline">
            <Input data-testid="edit-footer-text" value={content.footer_text || ''} onChange={(e) => set('footer_text', e.target.value)} />
          </Field>
        </SectionCard>
      </div>

      {/* Floating save */}
      <div className="sticky bottom-4 flex justify-end mt-8">
        <SaveBtn saving={saving} onClick={handleSave} testId="save-homepage-bottom-btn" shadow />
      </div>
    </div>
  );
}

/* ===== Sub-components ===== */

function AudienceSectionEditor({ sectionTitle, prefix, content, set }) {
  return (
    <SectionCard title={sectionTitle}>
      <Field label="Title">
        <Input
          data-testid={`edit-${prefix}-title`}
          value={content[`${prefix}_title`] || ''}
          onChange={(e) => set(`${prefix}_title`, e.target.value)}
        />
      </Field>
      <Field label="Short Description">
        <Input
          data-testid={`edit-${prefix}-short-description`}
          value={content[`${prefix}_short_description`] || ''}
          onChange={(e) => set(`${prefix}_short_description`, e.target.value)}
        />
      </Field>
      <Field label="Content Area (rich text)">
        <RichTextEditor
          value={content[`${prefix}_content_area`] || ''}
          onChange={(v) => set(`${prefix}_content_area`, v)}
          placeholder={`${sectionTitle} content...`}
        />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="CTA Label">
          <Input
            data-testid={`edit-${prefix}-cta-text`}
            value={content[`${prefix}_cta_text`] || ''}
            onChange={(e) => set(`${prefix}_cta_text`, e.target.value)}
          />
        </Field>
        <Field label="CTA Destination">
          <Input
            data-testid={`edit-${prefix}-cta-url`}
            value={content[`${prefix}_cta_url`] || ''}
            onChange={(e) => set(`${prefix}_cta_url`, e.target.value)}
          />
        </Field>
      </div>
    </SectionCard>
  );
}

function SectionCard({ title, children }) {
  return (
    <Card className="p-6 border border-[#e0e0e0]" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-').replace(/[?]/g, '')}`}>
      <h2 className="text-lg font-bold text-[#222] mb-5 pb-3 border-b border-[#eee]">{title}</h2>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-medium text-[#444] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function SaveBtn({ saving, onClick, testId, shadow }) {
  return (
    <Button data-testid={testId} onClick={onClick} disabled={saving} className={`bg-brand hover:bg-brand-hover text-white btn-uppercase gap-2 ${shadow ? 'shadow-lg' : ''}`}>
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
      Save Changes
    </Button>
  );
}

function ImageField({ label, field, value, uploading, onUpload }) {
  const inputRef = useRef(null);

  return (
    <div className="space-y-2" data-testid={`image-field-${field}`}>
      <Label className="text-sm font-medium text-[#444]">{label}</Label>
      {value && (
        <div className="w-full max-w-md rounded-lg overflow-hidden border border-[#ddd] bg-[#fafafa]">
          <img src={resolveImg(value)} alt={label} className="w-full h-48 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      )}
      <div className="flex items-center gap-3">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(field, e.target.files?.[0])} />
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} className="gap-2" data-testid={`upload-${field}-btn`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading...' : 'Upload New Image'}
        </Button>
        {value && <span className="text-xs text-[#999] truncate max-w-xs">{value.length > 60 ? '...' + value.slice(-40) : value}</span>}
      </div>
    </div>
  );
}

export default HomepageContentEditor;
